/**
 * Hybrid Account Service
 * 
 * Service that can use either Supabase or the mock API based on configuration
 */

import { 
  Account, 
  CreateAccountRequest, 
  UpdateAccountRequest, 
  CloseAccountRequest,
  AccountListParams,
  AccountListResponse,
  AccountType
} from '../types/account.types';
import { supabaseAccountService } from './supabase-service';
import { frontendAccountService } from './frontend-account-service';
import { getCurrentUserId } from '../lib/supabase';
import { devLog } from '../utils/logger';

export interface ApiResponse<T> {
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

export type DataSource = 'supabase' | 'api';

export class HybridAccountService {
  private dataSource: DataSource;
  private apiService: typeof frontendAccountService;
  private supabaseService: typeof supabaseAccountService;

  constructor(dataSource: DataSource = 'api') {
    this.dataSource = dataSource;
    this.apiService = frontendAccountService;
    this.supabaseService = supabaseAccountService;
  }

  setDataSource(source: DataSource): void {
    this.dataSource = source;
  }

  getDataSource(): DataSource {
    return this.dataSource;
  }

  setAuthToken(token: string): void {
    this.apiService.setAuthToken(token);
  }

  /**
   * Get current authenticated user ID from Supabase
   * 
   * @returns Promise<string | null> - The authenticated user ID, or null if not authenticated
   */
  private async getCurrentUserId(): Promise<string | null> {
    try {
      return await getCurrentUserId();
    } catch (error) {
      console.error('Error getting current user ID:', error);
      return null;
    }
  }

  async getAccounts(params: AccountListParams = {}): Promise<ApiResponse<AccountListResponse>> {
    if (this.dataSource === 'supabase') {
      try {
        // Convert API params to Supabase format
        const filters = {
          type: params.type,
          status: params.status as 'active' | 'closed' | undefined,
          includeInNetWorth: params.includeInNetWorth,
          category: params.category,
          page: params.page,
          limit: params.limit
        };

        const userId = await this.getCurrentUserId();
        if (!userId) {
          return {
            error: {
              code: 'AUTH_ERROR',
              message: 'User not authenticated. Please sign in.',
              details: 'No authenticated user found'
            }
          };
        }

        const result = await this.supabaseService.getAccounts(userId, filters);
        
        // Convert Supabase response to API format
        const accounts = result.accounts.map(this.convertSupabaseAccountToApi);
        const totalPages = Math.ceil(result.total / (params.limit || 20));
        
        return {
          data: {
            accounts,
            pagination: {
              page: params.page || 1,
              limit: params.limit || 20,
              total: result.total,
              totalPages,
              hasNext: (params.page || 1) < totalPages,
              hasPrev: (params.page || 1) > 1
            }
          }
        };
      } catch (error) {
        return {
          error: {
            code: 'SUPABASE_ERROR',
            message: error instanceof Error ? error.message : 'Unknown Supabase error',
            details: error
          }
        };
      }
    } else {
      return this.apiService.getAccounts(params);
    }
  }

  async getAccountById(accountId: string): Promise<ApiResponse<Account>> {
    if (this.dataSource === 'supabase') {
      try {
        const userId = await this.getCurrentUserId();
        if (!userId) {
          return {
            error: {
              code: 'AUTH_ERROR',
              message: 'User not authenticated. Please sign in.',
              details: 'No authenticated user found'
            }
          };
        }

        const account = await this.supabaseService.getAccountById(accountId, userId);
        return {
          data: this.convertSupabaseAccountToApi(account)
        };
      } catch (error) {
        return {
          error: {
            code: 'SUPABASE_ERROR',
            message: error instanceof Error ? error.message : 'Unknown Supabase error',
            details: error
          }
        };
      }
    } else {
      return this.apiService.getAccountById(accountId);
    }
  }

  async createAccount(accountData: CreateAccountRequest): Promise<ApiResponse<Account>> {
    if (this.dataSource === 'supabase') {
      try {
        const userId = await this.getCurrentUserId();
        if (!userId) {
          return {
            error: {
              code: 'AUTH_ERROR',
              message: 'User not authenticated. Please sign in.',
              details: 'No authenticated user found'
            }
          };
        }

        // For assets, map purchaseDate to openingDate if openingDate is not provided
        // This allows CreateAssetRequest to use purchaseDate without requiring openingDate
        const effectiveOpeningDate = accountData.type === AccountType.ASSETS && !accountData.openingDate && accountData.purchaseDate
          ? accountData.purchaseDate
          : accountData.openingDate;

        const account = await this.supabaseService.createAccount({
          userId: userId,
          name: accountData.name,
          type: accountData.type,
          provider: accountData.provider,
          accountNumber: accountData.accountNumber,
          openingBalance: accountData.openingBalance,
          currentBalance: accountData.currentBalance ?? accountData.openingBalance,
          openingDate: effectiveOpeningDate,
          isActive: true, // Always true for new accounts
          includeInNetWorth: accountData.includeInNetWorth ?? true,
          notes: accountData.notes,
          creditLimit: accountData.creditLimit,
          interestRate: accountData.interestRate,
          paymentDueDate: accountData.paymentDueDate,
          renewalDate: accountData.renewalDate,
          loanAmount: accountData.loanAmount,
          lastPaymentDue: accountData.lastPaymentDue,
          interestCalculatedDay: accountData.interestCalculatedDay,
          loanPurpose: accountData.loanPurpose,
          linkedAccountId: accountData.linkedAccountId,
          leaseEndDate: accountData.leaseEndDate,
          residualValue: accountData.residualValue,
          employerName: accountData.employerName,
          // Asset-specific fields
          category: accountData.category,
          subType: accountData.subType,
          currentValue: accountData.currentValue,
          purchasePrice: accountData.purchasePrice,
          purchaseDate: accountData.purchaseDate,
          valuationSource: accountData.valuationSource,
          valuationProvider: accountData.valuationProvider,
          lastValuationDate: accountData.lastValuationDate,
          valueBehaviour: accountData.valueBehaviour,
          depreciationMethod: accountData.depreciationMethod,
          depreciationRatePercent: accountData.depreciationRatePercent,
          autoUpdateValue: accountData.autoUpdateValue
        });
        
        return {
          data: this.convertSupabaseAccountToApi(account)
        };
      } catch (error) {
        return {
          error: {
            code: 'SUPABASE_ERROR',
            message: error instanceof Error ? error.message : 'Unknown Supabase error',
            details: error
          }
        };
      }
    } else {
      return this.apiService.createAccount(accountData);
    }
  }

  async updateAccount(accountId: string, updateData: UpdateAccountRequest): Promise<ApiResponse<Account>> {
    if (this.dataSource === 'supabase') {
      try {
        const userId = await this.getCurrentUserId();
        if (!userId) {
          return {
            error: {
              code: 'AUTH_ERROR',
              message: 'User not authenticated. Please sign in.',
              details: 'No authenticated user found'
            }
          };
        }

        const account = await this.supabaseService.updateAccount(accountId, userId, {
          name: updateData.name,
          type: updateData.type,
          provider: updateData.provider,
          accountNumber: updateData.accountNumber,
          openingBalance: updateData.openingBalance,
          currentBalance: updateData.currentBalance,
          openingDate: updateData.purchaseDate && !updateData.openingDate
            ? updateData.purchaseDate
            : updateData.openingDate,
          isActive: updateData.isActive,
          includeInNetWorth: updateData.includeInNetWorth,
          notes: updateData.notes,
          closureDate: updateData.closureDate,
          closureReason: updateData.closureReason,
          creditLimit: updateData.creditLimit,
          interestRate: updateData.interestRate,
          paymentDueDate: updateData.paymentDueDate,
          renewalDate: updateData.renewalDate,
          loanAmount: updateData.loanAmount,
          lastPaymentDue: updateData.lastPaymentDue,
          interestCalculatedDay: updateData.interestCalculatedDay,
          loanPurpose: updateData.loanPurpose,
          linkedAccountId: updateData.linkedAccountId,
          leaseEndDate: updateData.leaseEndDate,
          residualValue: updateData.residualValue,
          employerName: updateData.employerName,
          // Asset-specific fields
          category: updateData.category,
          subType: updateData.subType,
          currentValue: updateData.currentValue,
          purchasePrice: updateData.purchasePrice,
          purchaseDate: updateData.purchaseDate,
          valuationSource: updateData.valuationSource,
          valuationProvider: updateData.valuationProvider,
          lastValuationDate: updateData.lastValuationDate,
          valueBehaviour: updateData.valueBehaviour,
          depreciationMethod: updateData.depreciationMethod,
          depreciationRatePercent: updateData.depreciationRatePercent,
          autoUpdateValue: updateData.autoUpdateValue
        });
        
        return {
          data: this.convertSupabaseAccountToApi(account)
        };
      } catch (error) {
        return {
          error: {
            code: 'SUPABASE_ERROR',
            message: error instanceof Error ? error.message : 'Unknown Supabase error',
            details: error
          }
        };
      }
    } else {
      return this.apiService.updateAccount(accountId, updateData);
    }
  }

  async closeAccount(accountId: string, closeData: CloseAccountRequest): Promise<ApiResponse<Account>> {
    if (this.dataSource === 'supabase') {
      try {
        const userId = await this.getCurrentUserId();
        if (!userId) {
          return {
            error: {
              code: 'AUTH_ERROR',
              message: 'User not authenticated. Please sign in.',
              details: 'No authenticated user found'
            }
          };
        }

        const account = await this.supabaseService.closeAccount(accountId, userId, {
          closureDate: closeData.closureDate,
          closureReason: closeData.closureReason,
          includeInNetWorth: closeData.includeInNetWorth,
          currentValue: closeData.currentValue
        });
        
        return {
          data: this.convertSupabaseAccountToApi(account)
        };
      } catch (error) {
        return {
          error: {
            code: 'SUPABASE_ERROR',
            message: error instanceof Error ? error.message : 'Unknown Supabase error',
            details: error
          }
        };
      }
    } else {
      return this.apiService.closeAccount(accountId, closeData);
    }
  }

  async deleteAccount(accountId: string): Promise<ApiResponse<void>> {
    if (this.dataSource === 'supabase') {
      try {
        const userId = await this.getCurrentUserId();
        if (!userId) {
          return {
            error: {
              code: 'AUTH_ERROR',
              message: 'User not authenticated. Please sign in.',
              details: 'No authenticated user found'
            }
          };
        }

        await this.supabaseService.deleteAccount(accountId, userId);
        return { data: undefined };
      } catch (error) {
        return {
          error: {
            code: 'SUPABASE_ERROR',
            message: error instanceof Error ? error.message : 'Unknown Supabase error',
            details: error
          }
        };
      }
    } else {
      return this.apiService.deleteAccount(accountId);
    }
  }

  async exportToCSV(params: AccountListParams = {}, filename?: string): Promise<ApiResponse<Blob>> {
    if (this.dataSource === 'supabase') {
      try {
        const filters = {
          type: params.type,
          status: params.status as 'active' | 'closed' | undefined
        };
        
        const userId = await this.getCurrentUserId();
        if (!userId) {
          return {
            error: {
              code: 'AUTH_ERROR',
              message: 'User not authenticated. Please sign in.',
              details: 'No authenticated user found'
            }
          };
        }

        const csvContent = await this.supabaseService.exportAccountsToCSV(userId, filters);
        const blob = new Blob([csvContent], { type: 'text/csv' });
        
        return { data: blob };
      } catch (error) {
        return {
          error: {
            code: 'SUPABASE_ERROR',
            message: error instanceof Error ? error.message : 'Unknown Supabase error',
            details: error
          }
        };
      }
    } else {
      return this.apiService.exportToCSV(params, filename);
    }
  }

  async exportToExcel(params: AccountListParams = {}, filename?: string): Promise<ApiResponse<Blob>> {
    // Excel export is more complex, so we'll use the API service for now
    return this.apiService.exportToExcel(params, filename);
  }

  /**
   * Convenience method to export accounts as CSV and download
   */
  async exportAccountsCSV(): Promise<void> {
    try {
      const response = await this.exportToCSV();
      
      if (response.error) {
        throw new Error(response.error.message);
      }
      
      if (response.data) {
        this.downloadFile(response.data, 'accounts.csv');
      }
    } catch (error) {
      console.error('CSV export failed:', error);
      throw error;
    }
  }

  /**
   * Convenience method to export accounts as Excel and download
   */
  async exportAccountsExcel(): Promise<void> {
    try {
      const response = await this.exportToExcel();
      
      if (response.error) {
        throw new Error(response.error.message);
      }
      
      if (response.data) {
        this.downloadFile(response.data, 'accounts.xlsx');
      }
    } catch (error) {
      console.error('Excel export failed:', error);
      throw error;
    }
  }

  downloadFile(blob: Blob, filename: string): void {
    this.apiService.downloadFile(blob, filename);
  }

  formatCurrency(amount: number, currency: string = 'AUD'): string {
    return this.apiService.formatCurrency(amount, currency);
  }

  formatDate(dateString: string): string {
    return this.apiService.formatDate(dateString);
  }

  getAccountTypeDisplayName(type: AccountType): string {
    return this.apiService.getAccountTypeDisplayName(type);
  }

  // Helper method to convert Supabase account format to API format
  private convertSupabaseAccountToApi(supabaseAccount: any): Account {
    
    const result = {
      id: supabaseAccount.id,
      userId: supabaseAccount.user_id,
      name: supabaseAccount.name,
      type: supabaseAccount.type,
      provider: supabaseAccount.provider,
      accountNumber: supabaseAccount.account_number,
      openingBalance: supabaseAccount.opening_balance,
      currentBalance: supabaseAccount.current_balance,
      openingDate: supabaseAccount.opening_date,
      isActive: supabaseAccount.is_active,
      includeInNetWorth: supabaseAccount.include_in_net_worth,
      notes: supabaseAccount.notes,
      createdAt: supabaseAccount.created_at,
      updatedAt: supabaseAccount.updated_at,
      closureDate: supabaseAccount.closure_date,
      closureReason: supabaseAccount.closure_reason,
      creditLimit: supabaseAccount.credit_limit,
      interestRate: supabaseAccount.interest_rate,
      paymentDueDate: supabaseAccount.payment_due_date,
      renewalDate: supabaseAccount.renewal_date,
      loanAmount: supabaseAccount.loan_amount,
      lastPaymentDue: supabaseAccount.last_payment_due,
      interestCalculatedDay: supabaseAccount.interest_calculated_day ? Number(supabaseAccount.interest_calculated_day) : undefined,
      loanPurpose: supabaseAccount.loan_purpose,
      linkedAccountId: supabaseAccount.linked_account_id,
      leaseEndDate: supabaseAccount.lease_end_date,
      residualValue: supabaseAccount.residual_value,
      employerName: supabaseAccount.employer_name,
      // Asset-specific fields
      category: supabaseAccount.category,
      subType: supabaseAccount.sub_type,
      currentValue: supabaseAccount.current_value,
      purchasePrice: supabaseAccount.purchase_price,
      purchaseDate: supabaseAccount.purchase_date,
      valuationSource: supabaseAccount.valuation_source,
      valuationProvider: supabaseAccount.valuation_provider,
      lastValuationDate: supabaseAccount.last_valuation_date,
      valueBehaviour: supabaseAccount.value_behaviour,
      depreciationMethod: supabaseAccount.depreciation_method,
      depreciationRatePercent: supabaseAccount.depreciation_rate_percent,
      autoUpdateValue: supabaseAccount.auto_update_value
    };
    
    
    return result;
  }
}

// Export a default instance that can be configured
export const hybridAccountService = new HybridAccountService();

// Helper function to detect if Supabase is available
export function isSupabaseAvailable(): boolean {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    return !!(supabaseUrl && supabaseAnonKey);
  } catch {
    return false;
  }
}

// Auto-configure the service based on environment
if (isSupabaseAvailable()) {
  devLog('🔗 Supabase credentials detected, switching to Supabase data source');
  hybridAccountService.setDataSource('supabase');
} else {
  devLog('🔗 Using mock API data source (Supabase not configured)');
  hybridAccountService.setDataSource('api');
}
